import { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api';
import { safeSessionStorage } from '../utils/ssrSafe';

/**
 * Custom hook for fetching products with caching and performance optimizations
 * 
 * Features:
 * - Automatic cache in sessionStorage for faster repeat views
 * - Deduplicated API requests
 * - Loading and error state management 
 * - Optimistic updates
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.endpoint - API endpoint to fetch from
 * @param {Object} options.params - Query parameters for the API request
 * @param {boolean} options.useCache - Whether to use cache (default: true)
 * @param {number} options.cacheDuration - Cache duration in minutes (default: 5)
 * @returns {Object} { data, loading, error, refetch, updateItem }
 */
export function useProducts({
  endpoint = '/api/products',
  params = {},
  useCache = true,
  cacheDuration = 5,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Create a stable string representation of params for dependency arrays
  const stableParamsString = useMemo(() => {
    return Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
  }, [params]);

  // Create a cache key from endpoint and params
  const getCacheKey = useCallback(() => {
    // stableParamsString is already sorted and stringified
    return `originselect_data_${endpoint}_${stableParamsString}`;
  }, [endpoint, stableParamsString]);

  // Check if cache is valid (not expired)
  const isCacheValid = useCallback((timestamp) => {
    if (!timestamp) return false;
    
    const now = new Date().getTime();
    const expiry = timestamp + (cacheDuration * 60 * 1000);
    return now < expiry;
  }, [cacheDuration]);

  // Try to get data from cache
  const getFromCache = useCallback(() => {
    if (!useCache) return { data: null, timestamp: null };
    
    try {
      const cacheKey = getCacheKey();
      const cachedData = safeSessionStorage.getItem(cacheKey);
      
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        setLastFetched(parsed.timestamp || null);
        
        // Return both data and timestamp for validity checking
        return {
          data: parsed.data || null,
          timestamp: parsed.timestamp || null
        };
      }
    } catch (err) {
      console.warn('Error reading from cache:', err);
    }
    
    return { data: null, timestamp: null };
  }, [getCacheKey, useCache]);

  // Save data to cache
  const saveToCache = useCallback((data) => {
    if (!useCache) return;
    
    try {
      const cacheKey = getCacheKey();
      const timestamp = new Date().getTime();
      
      safeSessionStorage.setItem(
        cacheKey,
        JSON.stringify({ data, timestamp })
      );
      
      setLastFetched(timestamp);
    } catch (err) {
      console.warn('Error saving to cache:', err);
    }
  }, [getCacheKey, useCache]);

  // Update a single item in the data array
  const updateItem = useCallback((id, updatedData) => {
    setData(prevData => {
      // Handle case where prevData might be null or undefined
      if (!Array.isArray(prevData)) return prevData;
      
      const newData = prevData.map(item => 
        item._id === id ? { ...item, ...updatedData } : item
      );
      
      // Update cache with new data
      saveToCache(newData);
      
      return newData;
    });
  }, [saveToCache]);

  // Main fetch function
  const fetchData = useCallback(async (skipCache = false) => {
    setLoading(true);
    setError(null);
    
    try {
      // Try to get from cache first (if not explicitly skipping cache)
      if (!skipCache) {
        const { data: cachedData, timestamp } = getFromCache();
        
        if (cachedData && isCacheValid(timestamp)) {
          setData(cachedData);
          setLoading(false);
          return;
        }
      }
      
      // Fetch from API if cache miss or expired
      const response = await API.get(endpoint, { params });
      let fetchedData;
      
      // Handle different response data structures
      if (Array.isArray(response.data)) {
        fetchedData = response.data;
      } else if (typeof response.data === 'object' && response.data !== null) {
        // Check for common data fields in API responses
        fetchedData = response.data.products || 
                     response.data.data || 
                     response.data.items || 
                     response.data.results ||
                     response.data;
        
        // If we still don't have an array but have a single object, wrap it
        if (!Array.isArray(fetchedData)) {
          // If it's a product detail response, just use the object directly
          if (endpoint.includes('/products/') && !endpoint.includes('/products/categories')) {
            fetchedData = response.data;
          } else {
            fetchedData = [];
            console.warn('Expected array response but got:', response.data);
          }
        }
      } else {
        fetchedData = [];
        console.warn('Unexpected API response format:', response.data);
      }
      
      setData(fetchedData);
      saveToCache(fetchedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError({
        message: 'Failed to load products. Please try again later.',
        details: err?.message || 'Unknown error'
      });
      
      // Try to use stale cache as fallback if available
      const { data: cachedData } = getFromCache();
      if (cachedData) {
        setData(cachedData);
        // Flag error but don't let it block display
        setError({
          message: 'Using cached data. Some information might be outdated.',
          isStaleCache: true
        });
      }
    } finally {
      setLoading(false);
    }
  }, [endpoint, stableParamsString, getFromCache, isCacheValid, saveToCache]);

  // Exposed refetch function for manual refresh
  const refetch = useCallback(() => {
    return fetchData(true); // Skip cache when manually refetching
  }, [fetchData]);

  // Fetch on mount or when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch, updateItem };
}

export default useProducts;